/**
 * Calculator registry — single source of truth for which calculators exist.
 * Adding a new calc means: add the component file, then add an entry here.
 */

import React from 'react';
import type { Clave } from '../../../i18n/textos';
import { useIdioma } from '../../../i18n/idioma';
import { Layers, Square as SquareIcon, Box, Trees, LucideIcon } from 'lucide-react';
import type { EstimateItem } from '../../../types';
import MulchVolumeCalc from './calcs/MulchVolumeCalc';
import SodCalc from './calcs/SodCalc';
import ConcreteCalc from './calcs/ConcreteCalc';
import PlantSpacingCalc from './calcs/PlantSpacingCalc';

/** Shape every calculator component implements. */
export interface CalculatorProps {
  /** Drop the computed line(s) into the estimate. The shell auto-creates a
   *  zone named after the calculator (same plumbing as Templates), so the
   *  contractor can see where each takeoff came from. */
  onAddTemplateItems: (items: EstimateItem[], template: { id: string; name: string }) => void;
  /** Workspace pricebook keyed by catalog item id. Material lines look up
   *  cost here so the dropped line is already priced. */
  pricebookByHint: Map<string, number>;
  /** Workspace target margin (percent 0..99) — drives rate on labor/equipment. */
  targetMarginPercent: number;
  /** Workspace material markup (percent, >= 0) — drives rate on Material lines.
   *  Faltaba: sin él las cuatro calculadoras cotizaban el material con la
   *  fórmula de margen, que con el margen en 30% deja un markup de 42,857%
   *  —el "43%" que el contratista no reconocía— y un sobreprecio del 19%. */
  materialMarkupPercent: number;
}

export interface CalculatorDef {
  id: string;
  /* Claves, no texto: esto es una constante de módulo y no hay hook que valga.
     El `id` y el `badge` siguen siendo datos. */
  name: Clave;
  description: Clave;
  badge: 'Daily' | 'Per-job';
  Icon: LucideIcon;
  tradeIds?: string[];
  Component: React.ComponentType<CalculatorProps>;
}

export const CALCULATORS: CalculatorDef[] = [
  {
    id: 'mulch-volume',
    name: 'calc.mulch.nombre',
    description: 'calc.mulch.desc',
    badge: 'Daily',
    Icon: Layers,
    tradeIds: ['landscaping', 'cleaning'],
    Component: MulchVolumeCalc,
  },
  {
    id: 'sod',
    name: 'calc.sod.nombre',
    description: 'calc.sod.desc',
    badge: 'Per-job',
    Icon: SquareIcon,
    tradeIds: ['landscaping'],
    Component: SodCalc,
  },
  {
    id: 'concrete',
    name: 'calc.concreto.nombre',
    description: 'calc.concreto.desc',
    badge: 'Per-job',
    Icon: Box,
    tradeIds: ['concrete', 'general_contractor', 'landscaping', 'roofing'],
    Component: ConcreteCalc,
  },
  {
    id: 'plant-spacing',
    name: 'calc.plantas.nombre',
    description: 'calc.plantas.desc',
    badge: 'Per-job',
    Icon: Trees,
    tradeIds: ['landscaping'],
    Component: PlantSpacingCalc,
  },
];
